import './coapplicantEditResidencePage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { red } from '@mui/material/colors';
import LinearProgress from '@mui/material/LinearProgress';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AddressLineOne } from '../../../components/AddressLineOne/AddressLineOne';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { Select } from '../../../components/Select/Select';
import { inputEventHandler } from '../../../components/utils/inputEventHandler';
import { USStates } from '../../../store/addressSuggestionStore/constants/states';
import { housingTypeOptions } from '../../../store/applicantStore/constants/housingTypeOptions';
import { monthsAtResidenceOptions } from '../../../store/applicantStore/constants/monthsAtResidenceOptions';
import { ONE_YEAR_IN_MONTHS_AS_STRING } from '../../../store/applicantStore/constants/oneYear';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { setDrawerOpen } = useNavigationPaneStore();

  const {
    computed: { coapplicantReviewEditPathname, coapplicantReviewEditProgress },
    coapplicantCurrentResidenceCity,
    coapplicantCurrentResidenceLineOne,
    coapplicantCurrentResidenceLineTwo,
    coapplicantCurrentResidenceMonthlyPaymentAmount,
    coapplicantCurrentResidenceMonthsAt,
    coapplicantCurrentResidencePostalCode,
    coapplicantCurrentResidenceState,
    coapplicantCurrentResidenceType,
    coapplicantPreviousResidenceCity,
    coapplicantPreviousResidenceLineOne,
    coapplicantPreviousResidenceLineTwo,
    coapplicantPreviousResidencePostalCode,
    coapplicantPreviousResidenceState,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantCurrentResidenceCity,
    setCoapplicantCurrentResidenceLineOne,
    setCoapplicantCurrentResidenceLineTwo,
    setCoapplicantCurrentResidenceMonthlyPaymentAmount,
    setCoapplicantCurrentResidenceMonthsAt,
    setCoapplicantCurrentResidencePostalCode,
    setCoapplicantCurrentResidenceState,
    setCoapplicantCurrentResidenceType,
    setCoapplicantPreviousResidenceCity,
    setCoapplicantPreviousResidenceLineOne,
    setCoapplicantPreviousResidenceLineTwo,
    setCoapplicantPreviousResidencePostalCode,
    setCoapplicantPreviousResidenceState,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      currentCityValue: coapplicantCurrentResidenceCity,
      currentLineOneValue: coapplicantCurrentResidenceLineOne,
      currentLineTwoValue: coapplicantCurrentResidenceLineTwo,
      currentMonthsAtResidenceValue: coapplicantCurrentResidenceMonthsAt,
      currentPostalCodeValue: coapplicantCurrentResidencePostalCode,
      currentStateValue: coapplicantCurrentResidenceState,
      housingAmountValue: inputEventHandler.currency.formatter(
        coapplicantCurrentResidenceMonthlyPaymentAmount,
      ),
      housingTypeValue: coapplicantCurrentResidenceType,
      previousCityValue: coapplicantPreviousResidenceCity,
      previousLineOneValue: coapplicantPreviousResidenceLineOne,
      previousLineTwoValue: coapplicantPreviousResidenceLineTwo,
      previousPostalCodeValue: coapplicantPreviousResidencePostalCode,
      previousStateValue: coapplicantPreviousResidenceState,
    },
    delayError: 200,
    mode: 'onBlur',
    shouldFocusError: false,
  });

  const shouldShowPreviousResidenceFields =
    watch('currentMonthsAtResidenceValue') === ONE_YEAR_IN_MONTHS_AS_STRING;

  const housingTypeValidation = {
    required: t('validation.housingType.required'),
  };

  const housingAmountValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.housingAmount.required'),
    min: {
      value: 0,
      message: t('validation.housingAmount.min', { min: '$0' }),
    },
    max: {
      value: 1000000,
      message: t('validation.housingAmount.max', { max: '$1,000,000' }),
    },
  };

  const addressValidation = {
    required: t('validation.address.required'),
  };

  const cityValidation = {
    required: t('validation.city.required'),
  };

  const stateValidation = {
    required: t('validation.state.required'),
  };

  const postalCodeValidation = {
    required: t('validation.zipCode.required'),
    minLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
    maxLength: {
      value: 5,
      message: t('validation.zipCode.minMax', { minMax: 5 }),
    },
  };

  const monthsAtResidenceValidation = {
    required: t('validation.housingType.required'),
  };

  const finishEditing = (): void => {
    navigate(coapplicantReviewEditPathname);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      currentCityValue,
      currentLineOneValue,
      currentLineTwoValue,
      currentMonthsAtResidenceValue,
      currentPostalCodeValue,
      currentStateValue,
      housingAmountValue,
      housingTypeValue,
      previousCityValue,
      previousLineOneValue,
      previousLineTwoValue,
      previousPostalCodeValue,
      previousStateValue,
    } = values;

    if (
      !currentCityValue ||
      !currentLineOneValue ||
      !currentMonthsAtResidenceValue ||
      !currentPostalCodeValue ||
      !currentStateValue ||
      !housingAmountValue ||
      !housingTypeValue
    ) {
      return;
    }

    if (
      shouldShowPreviousResidenceFields &&
      (!previousLineOneValue ||
        !previousCityValue ||
        !previousStateValue ||
        !previousPostalCodeValue)
    ) {
      return;
    }

    setCoapplicantCurrentResidenceCity(currentCityValue);
    setCoapplicantCurrentResidenceLineOne(currentLineOneValue);
    setCoapplicantCurrentResidenceLineTwo(currentLineTwoValue);
    setCoapplicantCurrentResidenceMonthlyPaymentAmount(housingAmountValue);
    setCoapplicantCurrentResidenceMonthsAt(currentMonthsAtResidenceValue);
    setCoapplicantCurrentResidencePostalCode(currentPostalCodeValue);
    setCoapplicantCurrentResidenceState(currentStateValue);
    setCoapplicantCurrentResidenceType(housingTypeValue);

    if (shouldShowPreviousResidenceFields) {
      setCoapplicantPreviousResidenceCity(previousCityValue);
      setCoapplicantPreviousResidenceLineOne(previousLineOneValue);
      setCoapplicantPreviousResidenceLineTwo(previousLineTwoValue);
      setCoapplicantPreviousResidencePostalCode(previousPostalCodeValue);
      setCoapplicantPreviousResidenceState(previousStateValue);
    } else {
      setCoapplicantPreviousResidenceCity(null);
      setCoapplicantPreviousResidenceLineOne(null);
      setCoapplicantPreviousResidenceLineTwo(null);
      setCoapplicantPreviousResidencePostalCode(null);
      setCoapplicantPreviousResidenceState(null);
    }

    if (isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        finishEditing();
      }

      return;
    }

    finishEditing();
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="coapplicant-edit-residence-page" sx={theme.pageLayout}>
        <header
          data-testid={locator.pages.coapplicantEditResidence.header.root}
        >
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.coapplicantEditResidence.header.previousStepButton
            }
            type="button"
            onClick={finishEditing}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.coapplicantEditResidence.header.drawerButton
            }
            type="button"
            onClick={(): void => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={locator.pages.coapplicantEditResidence.progress.root}
            value={coapplicantReviewEditProgress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.coapplicantEditResidence.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.coapplicantEditResidence.form.h1}>
                {t('CoapplicantEditResidencePage.title')}
              </h1>
              <h2 data-testid={locator.pages.coapplicantEditResidence.form.h2}>
                {t('CoapplicantEditResidencePage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantEditResidence.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <Select
                    control={control}
                    errorTestId={
                      locator.pages.coapplicantEditResidence.form
                        .residenceTypeSelectError
                    }
                    inputTestId={
                      locator.pages.coapplicantEditResidence.form
                        .residenceTypeSelect
                    }
                    label={t('CoapplicantEditResidencePage.housingType')}
                    name="housingTypeValue"
                    options={housingTypeOptions}
                    rules={housingTypeValidation}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register('housingAmountValue', housingAmountValidation)}
                    error={!isEmpty(errors.housingAmountValue)}
                    label={t('CoapplicantEditResidencePage.housingAmount')}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.coapplicantEditResidence.form.amountInput,
                        inputMode: 'numeric',
                      },
                    }}
                    type="text"
                    variant="outlined"
                    fullWidth
                    onBlur={inputEventHandler.currency.blur(
                      'housingAmountValue',
                      setValue,
                      trigger,
                    )}
                    onChange={inputEventHandler.currency.change(
                      'housingAmountValue',
                      setValue,
                    )}
                    onFocus={inputEventHandler.currency.focus(
                      'housingAmountValue',
                      setValue,
                    )}
                    onKeyDown={inputEventHandler.number.keydown}
                    onPasteCapture={inputEventHandler.number.pasteCapture}
                  />

                  {!isEmpty(errors.housingAmountValue) && (
                    <span
                      data-testid={
                        locator.pages.coapplicantEditResidence.form
                          .amountInputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.housingAmountValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <AddressLineOne
                    errorTestId={
                      locator.pages.coapplicantEditResidence.form
                        .addressLine1InputError
                    }
                    errors={errors}
                    label={t('CoapplicantEditResidencePage.residenceLineOne')}
                    name="currentLineOneValue"
                    register={register}
                    setValue={setValue}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.coapplicantEditResidence.form
                            .addressLine1Input,
                      },
                    }}
                    suggestionFields={{
                      cityFieldName: 'currentCityValue',
                      stateFieldName: 'currentStateValue',
                      postalCodeFieldName: 'currentPostalCodeValue',
                    }}
                    validation={addressValidation}
                    variant="outlined"
                    fullWidth
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <TextField
                        {...register('currentLineTwoValue')}
                        autoComplete="address-line2"
                        label={t(
                          'CoapplicantEditResidencePage.residenceLineTwo',
                        )}
                        slotProps={{
                          htmlInput: {
                            'data-testid':
                              locator.pages.coapplicantEditResidence.form
                                .addressLine2Input,
                          },
                        }}
                        variant="outlined"
                        fullWidth
                      />
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register('currentCityValue', cityValidation)}
                        autoComplete="address-level2"
                        error={!isEmpty(errors.currentCityValue)}
                        label={t('CoapplicantEditResidencePage.residenceCity')}
                        slotProps={{
                          inputLabel: {
                            shrink: Boolean(watch('currentCityValue')),
                          },
                          htmlInput: {
                            'data-testid':
                              locator.pages.coapplicantEditResidence.form
                                .cityInput,
                          },
                        }}
                        variant="outlined"
                        fullWidth
                      />

                      {!isEmpty(errors.currentCityValue) && (
                        <span
                          data-testid={
                            locator.pages.coapplicantEditResidence.form
                              .cityInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.currentCityValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <div className="inline-inputs">
                    <div className="input-half">
                      <Select
                        control={control}
                        errorTestId={
                          locator.pages.coapplicantEditResidence.form
                            .stateSelectError
                        }
                        inputTestId={
                          locator.pages.coapplicantEditResidence.form
                            .stateSelect
                        }
                        label={t('CoapplicantEditResidencePage.residenceState')}
                        name="currentStateValue"
                        options={USStates.map(({ abbreviation, name }) => ({
                          name,
                          value: abbreviation,
                        }))}
                        rules={stateValidation}
                      />
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register(
                          'currentPostalCodeValue',
                          postalCodeValidation,
                        )}
                        autoComplete="postal-code"
                        error={!isEmpty(errors.currentPostalCodeValue)}
                        label={t('CoapplicantEditResidencePage.zip')}
                        slotProps={{
                          inputLabel: {
                            shrink: Boolean(watch('currentPostalCodeValue')),
                          },
                          htmlInput: {
                            'data-testid':
                              locator.pages.coapplicantEditResidence.form
                                .zipCodeInput,
                            inputMode: 'numeric',
                          },
                        }}
                        type="number"
                        variant="outlined"
                        fullWidth
                        onKeyDown={inputEventHandler.number.keydown}
                        onPasteCapture={inputEventHandler.number.pasteCapture}
                      />

                      {!isEmpty(errors.currentPostalCodeValue) && (
                        <span
                          data-testid={
                            locator.pages.coapplicantEditResidence.form
                              .zipCodeInputError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.currentPostalCodeValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <Select
                    control={control}
                    errorTestId={
                      locator.pages.coapplicantEditResidence.form
                        .monthSelectError
                    }
                    inputTestId={
                      locator.pages.coapplicantEditResidence.form.monthSelect
                    }
                    label={t('CoapplicantEditResidencePage.duration')}
                    name="currentMonthsAtResidenceValue"
                    options={monthsAtResidenceOptions}
                    rules={monthsAtResidenceValidation}
                  />
                </div>
              </div>

              {shouldShowPreviousResidenceFields && (
                <div
                  data-testid="previous-residence-section"
                  style={{ marginBottom: '3rem' }}
                >
                  <h3>{t('CoapplicantEditResidencePage.previousResidence')}</h3>

                  <div style={{ marginTop: '1.5rem' }}>
                    <AddressLineOne
                      errorTestId={
                        locator.pages.coapplicantEditResidence.form
                          .previousAddressLine1InputError
                      }
                      errors={errors}
                      label={t('CoapplicantEditResidencePage.residenceLineOne')}
                      name="previousLineOneValue"
                      register={register}
                      setValue={setValue}
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.coapplicantEditResidence.form
                              .previousAddressLine1Input,
                        },
                      }}
                      suggestionFields={{
                        cityFieldName: 'previousCityValue',
                        stateFieldName: 'previousStateValue',
                        postalCodeFieldName: 'previousPostalCodeValue',
                      }}
                      validation={addressValidation}
                      variant="outlined"
                      fullWidth
                    />
                  </div>

                  <div style={{ marginTop: '1.5rem' }}>
                    <div className="inline-inputs">
                      <div className="input-half">
                        <TextField
                          {...register('previousLineTwoValue')}
                          autoComplete="address-line2"
                          label={t(
                            'CoapplicantEditResidencePage.residenceLineTwo',
                          )}
                          slotProps={{
                            htmlInput: {
                              'data-testid':
                                locator.pages.coapplicantEditResidence.form
                                  .previousAddressLine2Input,
                            },
                          }}
                          variant="outlined"
                          fullWidth
                        />
                      </div>

                      <div className="input-half">
                        <TextField
                          {...register('previousCityValue', cityValidation)}
                          autoComplete="address-level2"
                          error={!isEmpty(errors.previousCityValue)}
                          label={t(
                            'CoapplicantEditResidencePage.residenceCity',
                          )}
                          slotProps={{
                            inputLabel: {
                              shrink: Boolean(watch('previousCityValue')),
                            },
                            htmlInput: {
                              'data-testid':
                                locator.pages.coapplicantEditResidence.form
                                  .previousCityInput,
                            },
                          }}
                          variant="outlined"
                          fullWidth
                        />

                        {!isEmpty(errors.previousCityValue) && (
                          <span
                            data-testid={
                              locator.pages.coapplicantEditResidence.form
                                .previousCityInputError
                            }
                            style={{ fontSize: '12px', color: red[700] }}
                          >
                            {toString(errors.previousCityValue.message)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: '1.5rem' }}>
                    <div className="inline-inputs">
                      <div className="input-half">
                        <Select
                          control={control}
                          errorTestId={
                            locator.pages.coapplicantEditResidence.form
                              .previousStateSelectError
                          }
                          inputTestId={
                            locator.pages.coapplicantEditResidence.form
                              .previousStateSelect
                          }
                          label={t(
                            'CoapplicantEditResidencePage.residenceState',
                          )}
                          name="previousStateValue"
                          options={USStates.map(({ abbreviation, name }) => ({
                            name,
                            value: abbreviation,
                          }))}
                          rules={stateValidation}
                        />
                      </div>

                      <div className="input-half">
                        <TextField
                          {...register(
                            'previousPostalCodeValue',
                            postalCodeValidation,
                          )}
                          autoComplete="postal-code"
                          error={!isEmpty(errors.previousPostalCodeValue)}
                          label={t('CoapplicantEditResidencePage.zip')}
                          slotProps={{
                            inputLabel: {
                              shrink: Boolean(watch('previousPostalCodeValue')),
                            },
                            htmlInput: {
                              'data-testid':
                                locator.pages.coapplicantEditResidence.form
                                  .previousZipCodeInput,
                              inputMode: 'numeric',
                            },
                          }}
                          type="number"
                          variant="outlined"
                          fullWidth
                          onKeyDown={inputEventHandler.number.keydown}
                          onPasteCapture={inputEventHandler.number.pasteCapture}
                        />

                        {!isEmpty(errors.previousPostalCodeValue) && (
                          <span
                            data-testid={
                              locator.pages.coapplicantEditResidence.form
                                .previousZipCodeInputError
                            }
                            style={{ fontSize: '12px', color: red[700] }}
                          >
                            {toString(errors.previousPostalCodeValue.message)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-buttons">
                  <Button
                    color="secondary"
                    data-testid={
                      locator.pages.coapplicantEditResidence.form.cancelButton
                    }
                    loadingPosition="start"
                    startIcon={<></>}
                    type="button"
                    variant="contained"
                    fullWidth
                    onClick={finishEditing}
                  >
                    {t('CoapplicantEditResidencePage.cancel')}
                  </Button>

                  <Button
                    color="primary"
                    data-testid={
                      locator.pages.coapplicantEditResidence.form.submitButton
                    }
                    disabled={!isValid}
                    loading={isSyncingCoapplicant}
                    startIcon={<></>}
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {t('CoapplicantEditResidencePage.save')}
                  </Button>
                </div>
              </div>
            </div>
            <footer
              data-testid={locator.pages.coapplicantEditResidence.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantEditResidencePage';
